import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  TrendingDown, AlertTriangle, CheckCircle, Clock, FileText,
  Loader2, Eye, Archive, RotateCcw, Activity, Shield, Download, Mail
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KnowledgeDegradationPage = () => {
  const { token } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetHistory, setAssetHistory] = useState(null);
  const [reviewNote, setReviewNote] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => { fetchAnalysis(); }, []);

  const fetchAnalysis = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/knowledge-degradation/analysis`, { headers: { Authorization: `Bearer ${token}` } });
      setAnalysis(res.data);
    } catch { toast.error('Failed to load degradation analysis'); }
    finally { setLoading(false); }
  };

  const viewAssetHistory = async (asset) => {
    setSelectedAsset(asset);
    try {
      const res = await axios.get(`${API}/knowledge-degradation/${asset.asset_id}/history`, { headers: { Authorization: `Bearer ${token}` } });
      setAssetHistory(res.data);
    } catch { setAssetHistory(null); }
  };

  const handleReview = async (assetId, action) => {
    try {
      await axios.post(`${API}/knowledge-degradation/${assetId}/review`, { action, note: reviewNote }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Asset ${action}`);
      setSelectedAsset(null);
      setReviewNote('');
      fetchAnalysis();
    } catch { toast.error('Failed'); }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'critical': return 'bg-[#FF3B30] text-white';
      case 'high': return 'bg-[#FF9500] text-white';
      case 'medium': return 'bg-[#FFCC00] text-[#0A0A0A]';
      default: return 'bg-[#34C759] text-white';
    }
  };

  const getDegradationBarColor = (score) => {
    if (score >= 80) return 'bg-[#FF3B30]';
    if (score >= 60) return 'bg-[#FF9500]';
    if (score >= 40) return 'bg-[#FFCC00]';
    return 'bg-[#34C759]';
  };

  const getHealthColor = (score) => {
    if (score >= 80) return 'text-[#34C759]';
    if (score >= 60) return 'text-[#FF9500]';
    return 'text-[#FF3B30]';
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div>;

  return (
    <div data-testid="knowledge-degradation-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Knowledge Degradation Tracking</h1>
          <p className="text-[#4B5563] text-sm">Monitor access patterns and predict knowledge staleness</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={async () => {
            try { const res = await axios.get(`${API}/knowledge-degradation/export-pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }); const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })); const a = document.createElement('a'); a.href = url; a.download = 'degradation-report.pdf'; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url); } catch { toast.error('Download failed'); }
          }} data-testid="export-pdf-btn"><Download className="w-3.5 h-3.5 mr-1" />PDF</Button>
          <Button size="sm" variant="outline" onClick={async () => {
            const emails = prompt('Enter email addresses (comma-separated):'); if (!emails) return;
            const message = prompt('Add a personal message (optional):') || '';
            try { const res = await axios.post(`${API}/knowledge-degradation/share`, { to_emails: emails.split(',').map(e => e.trim()).filter(Boolean), message }, { headers: { Authorization: `Bearer ${token}` } }); toast.success(res.data.message); } catch (e) { toast.error(e.response?.data?.detail || 'Share failed'); }
          }} data-testid="share-email-btn"><Mail className="w-3.5 h-3.5 mr-1" />Share</Button>
          <Button variant="outline" onClick={fetchAnalysis} data-testid="refresh-btn"><RotateCcw className="w-4 h-4 mr-2" />Refresh</Button>
        </div>
      </div>

      {!analysis ? (
        <Card className="border-[#E5E7EB]"><CardContent className="text-center py-16"><TrendingDown className="w-16 h-16 mx-auto mb-4 text-[#E5E7EB]" /><p className="text-[#9CA3AF]">No degradation data available</p></CardContent></Card>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="degradation-summary">
            <Card className="border-[#E5E7EB]">
              <CardContent className="p-4 text-center">
                <Shield className={`w-5 h-5 mx-auto mb-2 ${getHealthColor(analysis.health_score)}`} />
                <p className={`text-2xl font-bold ${getHealthColor(analysis.health_score)}`}>{analysis.health_score}%</p>
                <p className="text-xs text-[#9CA3AF]">Health Score</p>
              </CardContent>
            </Card>
            <Card className="border-[#E5E7EB]">
              <CardContent className="p-4 text-center">
                <AlertTriangle className="w-5 h-5 mx-auto mb-2 text-[#FF3B30]" />
                <p className="text-2xl font-bold text-[#FF3B30]">{analysis.summary?.critical || 0}</p>
                <p className="text-xs text-[#9CA3AF]">Critical</p>
              </CardContent>
            </Card>
            <Card className="border-[#E5E7EB]">
              <CardContent className="p-4 text-center">
                <AlertTriangle className="w-5 h-5 mx-auto mb-2 text-[#FF9500]" />
                <p className="text-2xl font-bold text-[#FF9500]">{analysis.summary?.high || 0}</p>
                <p className="text-xs text-[#9CA3AF]">High Risk</p>
              </CardContent>
            </Card>
            <Card className="border-[#E5E7EB]">
              <CardContent className="p-4 text-center">
                <Clock className="w-5 h-5 mx-auto mb-2 text-[#FFCC00]" />
                <p className="text-2xl font-bold">{analysis.review_queue_count || 0}</p>
                <p className="text-xs text-[#9CA3AF]">Need Review</p>
              </CardContent>
            </Card>
            <Card className="border-[#E5E7EB]">
              <CardContent className="p-4 text-center">
                <FileText className="w-5 h-5 mx-auto mb-2 text-[#002FA7]" />
                <p className="text-2xl font-bold">{analysis.total_assets || 0}</p>
                <p className="text-xs text-[#9CA3AF]">Total Assets</p>
              </CardContent>
            </Card>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="overview" data-testid="degradation-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="review" data-testid="degradation-tab-review">Review Queue ({analysis.review_queue_count || 0})</TabsTrigger>
              <TabsTrigger value="all" data-testid="degradation-tab-all">All Assets</TabsTrigger>
            </TabsList>

            {/* Overview */}
            <TabsContent value="overview">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Degradation by Type */}
                <Card className="border-[#E5E7EB]">
                  <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4" />Average Degradation by Type</CardTitle></CardHeader>
                  <CardContent>
                    {Object.entries(analysis.degradation_by_type || {}).map(([type, stats]) => (
                      <div key={type} className="flex items-center justify-between py-2.5 border-b border-[#F3F4F6] last:border-0">
                        <span className="text-sm capitalize">{type.replace('_', ' ')}</span>
                        <div className="flex items-center gap-3">
                          <div className="w-20 h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${getDegradationBarColor(stats.avg_degradation)}`} style={{ width: `${stats.avg_degradation}%` }} />
                          </div>
                          <span className="text-sm font-medium w-10 text-right">{stats.avg_degradation}%</span>
                          <span className="text-xs text-[#9CA3AF] w-6">{stats.count}</span>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Most At-Risk */}
                <Card className="border-[#E5E7EB]">
                  <CardHeader><CardTitle className="text-sm flex items-center gap-2"><TrendingDown className="w-4 h-4 text-[#FF3B30]" />Most At-Risk Assets</CardTitle></CardHeader>
                  <CardContent>
                    {(analysis.at_risk_assets || []).slice(0, 8).map((a) => (
                      <div key={a.asset_id} className="flex items-center justify-between py-2.5 border-b border-[#F3F4F6] last:border-0 cursor-pointer hover:bg-[#F9FAFB] -mx-4 px-4" onClick={() => viewAssetHistory(a)} data-testid={`at-risk-${a.asset_id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{a.title}</p>
                          <p className="text-xs text-[#9CA3AF]">{a.asset_type} · {a.days_since_access}d ago</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Badge className={getRiskColor(a.risk)} data-testid={`risk-badge-${a.asset_id}`}>{a.degradation_score}</Badge>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Review Queue */}
            <TabsContent value="review">
              <Card className="border-[#E5E7EB]">
                <CardHeader>
                  <CardTitle className="text-sm">Proactive Review Queue — assets predicted to become stale soon</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {(analysis.review_queue || []).length === 0 ? (
                    <div className="text-center py-12 text-[#9CA3AF]"><CheckCircle className="w-10 h-10 mx-auto mb-3 text-[#34C759]" /><p>No assets need immediate review</p></div>
                  ) : (
                    <div className="divide-y divide-[#E5E7EB]">
                      {analysis.review_queue.map((a) => (
                        <div key={a.asset_id} className="p-4 flex items-center gap-4" data-testid={`review-item-${a.asset_id}`}>
                          <div className="flex-shrink-0">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold ${a.risk === 'critical' ? 'bg-[#FF3B30]' : 'bg-[#FF9500]'}`}>
                              {a.degradation_score}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{a.title}</p>
                            <div className="flex items-center gap-3 text-xs text-[#9CA3AF] mt-0.5">
                              <span className="capitalize">{a.asset_type.replace('_', ' ')}</span>
                              <span>{a.days_since_access} days since last access</span>
                              {a.predicted_stale_in_days > 0 && <span className="text-[#FF9500]">Stale in ~{a.predicted_stale_in_days}d</span>}
                              {a.predicted_stale_in_days === 0 && <span className="text-[#FF3B30] font-medium">Already stale</span>}
                            </div>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => viewAssetHistory(a)}><Eye className="w-3 h-3 mr-1" />Details</Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-[#34C759]" onClick={() => handleReview(a.asset_id, 'reviewed')} data-testid={`review-btn-${a.asset_id}`}>
                              <CheckCircle className="w-3 h-3 mr-1" />Reviewed
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-[#9CA3AF]" onClick={() => handleReview(a.asset_id, 'snoozed')}>
                              <Clock className="w-3 h-3 mr-1" />Snooze
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* All Assets */}
            <TabsContent value="all">
              <Card className="border-[#E5E7EB]">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="all-assets-table">
                      <thead className="bg-[#F3F4F6]">
                        <tr>
                          <th className="text-left p-3 font-medium">Asset</th>
                          <th className="text-left p-3 font-medium">Type</th>
                          <th className="text-center p-3 font-medium">Days Idle</th>
                          <th className="text-center p-3 font-medium">90d Accesses</th>
                          <th className="text-center p-3 font-medium">Degradation Score</th>
                          <th className="text-center p-3 font-medium">Risk</th>
                          <th className="text-center p-3 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(analysis.all_assets || []).slice(0, 50).map((a) => (
                          <tr key={a.asset_id} className="border-t border-[#E5E7EB] hover:bg-[#F9FAFB] cursor-pointer" onClick={() => viewAssetHistory(a)}>
                            <td className="p-3 max-w-[200px] truncate">{a.title}</td>
                            <td className="p-3 capitalize text-[#9CA3AF]">{a.asset_type.replace('_', ' ')}</td>
                            <td className="text-center p-3">{a.days_since_access}</td>
                            <td className="text-center p-3">{a.recent_accesses_90d}</td>
                            <td className="text-center p-3">
                              <div className="flex items-center justify-center gap-1">
                                <div className="w-12 h-1.5 bg-[#E5E7EB] rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${getDegradationBarColor(a.degradation_score)}`} style={{ width: `${a.degradation_score}%` }} />
                                </div>
                                <span className="text-xs font-medium">{a.degradation_score}</span>
                              </div>
                            </td>
                            <td className="text-center p-3"><Badge className={getRiskColor(a.risk)}>{a.risk}</Badge></td>
                            <td className="text-center p-3"><Badge variant="outline" className="text-[10px]">{a.review_status || 'pending'}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Asset Detail Dialog */}
      <Dialog open={!!selectedAsset} onOpenChange={(v) => { if (!v) { setSelectedAsset(null); setAssetHistory(null); } }}>
        <DialogContent className="max-w-lg" data-testid="asset-detail-dialog">
          <DialogHeader><DialogTitle>Asset Details</DialogTitle></DialogHeader>
          {selectedAsset && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold">{selectedAsset.title}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={getRiskColor(selectedAsset.risk)}>{selectedAsset.risk} risk</Badge>
                  <span className="text-xs text-[#9CA3AF]">Degradation: {selectedAsset.degradation_score}%</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-[#9CA3AF]">Type</span><p className="font-medium capitalize">{selectedAsset.asset_type.replace('_', ' ')}</p></div>
                <div><span className="text-[#9CA3AF]">Days Since Access</span><p className="font-medium">{selectedAsset.days_since_access}</p></div>
                <div><span className="text-[#9CA3AF]">90-Day Accesses</span><p className="font-medium">{selectedAsset.recent_accesses_90d}</p></div>
                <div><span className="text-[#9CA3AF]">Predicted Stale In</span><p className="font-medium">{selectedAsset.predicted_stale_in_days > 0 ? `${selectedAsset.predicted_stale_in_days} days` : 'Already stale'}</p></div>
              </div>

              {/* Weekly Trend */}
              {assetHistory?.weekly_trend && (
                <div>
                  <Label className="text-xs text-[#9CA3AF] uppercase">12-Week Access Trend</Label>
                  <div className="flex items-end gap-1 mt-2 h-16" data-testid="weekly-trend">
                    {assetHistory.weekly_trend.map((w, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                        <div className={`w-full rounded-t ${w.accesses > 0 ? 'bg-[#002FA7]' : 'bg-[#E5E7EB]'}`} style={{ height: `${Math.max(4, (w.accesses / Math.max(1, ...assetHistory.weekly_trend.map(x => x.accesses))) * 48)}px` }} />
                        <span className="text-[8px] text-[#9CA3AF]">{w.week_label?.split(' ')[0]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Review Actions */}
              <div className="border-t border-[#E5E7EB] pt-4">
                <Label className="text-xs text-[#9CA3AF] uppercase">Review Actions</Label>
                <Textarea value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} placeholder="Optional note..." rows={2} className="mt-2" />
                <div className="flex gap-2 mt-2">
                  <Button size="sm" className="bg-[#34C759] hover:bg-[#2BAF4A]" onClick={() => handleReview(selectedAsset.asset_id, 'reviewed')} data-testid="mark-reviewed-btn">
                    <CheckCircle className="w-3.5 h-3.5 mr-1" />Mark Reviewed
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleReview(selectedAsset.asset_id, 'snoozed')}>
                    <Clock className="w-3.5 h-3.5 mr-1" />Snooze
                  </Button>
                  <Button size="sm" variant="outline" className="text-[#FF3B30]" onClick={() => handleReview(selectedAsset.asset_id, 'archived')}>
                    <Archive className="w-3.5 h-3.5 mr-1" />Archive
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KnowledgeDegradationPage;
